import React, { useState, useEffect, useRef } from "react";
import {
  IconArrowCascadeRight,
  IconArrowDropDown,
  IconCheckCircle,
  Button,
  BUTTON_VARIANTS,
  BUTTON_BACKGROUND_APPEARANCES,
  BUTTON_SIZES
} from "cdk-radial";
import "./DAABPricingPlanTile.css";
import {
  getPlanName,
  formatNumbers,
  formatCurrency,
  PLAN_TYPE,
  getPlanCardText,
  getPlanCTA
} from "../../utils/api-pricing";
import { API_DIRECTORY_THEME } from "../../utils/constants";
import { useDirectoryType } from "../../contexts/DirectoryTypeContext";

const DAABPricingPlanTile = props => {
  const belongsTo = useDirectoryType();

  const {
    api,
    plan,
    isAsync,
    allEndpoints,
    instanceIdVersionMap,
    selectPricingPlan,
    openBundlePriceDialog,
    isSelected,
    isAPIinCart,
    isAPIContracted,
    isAPIPurchased,
    isLoading,
    isMsaConsented,
    expandContainerDetails,
    isBundlePricing,
    fetchingAPIDetailsInBundle,
    isCurrentApiInBundleCart,
    isBundle
  } = props;
  const originalPlanType =
    plan.type?.toLowerCase() || plan.planType?.toLowerCase();
  const effectivePlanType =
    isBundle && originalPlanType === PLAN_TYPE.SUBSCRIPTION
      ? PLAN_TYPE.BUNDLE_SUBSCRIPTION
      : originalPlanType;

  const planCTA = getPlanCTA(isAPIinCart, isSelected);
  const PLAN_CARD_TEXT = getPlanCardText(belongsTo);
  const [pricingPlanToBeAdded, setPricingPlanToBeAdded] = useState("");

  useEffect(() => {
    if (!isLoading) setPricingPlanToBeAdded("");
  }, [isLoading]);

  return (
    <div
      data-testid="api-dir-pkg_daab-plan-tile"
      className={`api-dir-pkg_daab-plan-tile ${
        isSelected ? "api-dir-pkg_daab-selected-plan" : ""
      }`}
    >
      {effectivePlanType.toLowerCase() === PLAN_TYPE.ADVANCED.toLowerCase() ? (
        <PlanDetails
          details={{
            type: isBundle
              ? plan.planName
              : plan.plan === PLAN_TYPE.CUSTOM
              ? plan.name
              : plan.plan,
            price: plan.price,
            description: plan.description,
            displayName: isBundle ? plan.planName : null,
            installFee: plan?.installFee,
            isDealerPlan: plan?.isDealerPlan
          }}
          isBundle={isBundle}
          planType={effectivePlanType}
        />
      ) : (
        <PlanDetails
          details={{
            type: effectivePlanType,
            price: plan?.subscriptionPrice || plan?.price,
            description: PLAN_CARD_TEXT[effectivePlanType]?.subtext || "",
            displayName: isBundle ? plan.planName : null,
            installFee: plan?.installFee,
            isDealerPlan: plan?.isDealerPlan
          }}
          isBundle={isBundle}
          planType={effectivePlanType}
        />
      )}
      {effectivePlanType.toLowerCase() === PLAN_TYPE.ADVANCED.toLowerCase() ? (
        <EndpointContainers
          plan={plan}
          isAsync={isAsync}
          allEndpoints={allEndpoints}
          instanceIdVersionMap={instanceIdVersionMap}
          expandContainerDetails={expandContainerDetails}
          api={api}
          isBundlePricing={isBundlePricing}
          isBundle={isBundle}
          fetchingAPIDetailsInBundle={fetchingAPIDetailsInBundle}
        />
      ) : (
        <LegacyPlanContainers
          type={effectivePlanType}
          tiers={plan.rates || plan.tiers}
          freemiumUnit={plan.freemiumUnit}
          isBundle={isBundle}
        />
      )}

      <div className="api-dir-pkg_daab-plan-btn-container">
        {!isAPIContracted &&
          !isAPIPurchased &&
          belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY && (
            <Button
              dataTestId="button"
              onClick={() => {
                setPricingPlanToBeAdded(plan?.id);
                selectPricingPlan(plan?.id);
              }}
              isDisabled={
                isSelected || !isMsaConsented || isCurrentApiInBundleCart
              }
              isLoading={plan?.id === pricingPlanToBeAdded && isLoading}
              text={planCTA}
              size={BUTTON_SIZES.STANDARD}
              variant={BUTTON_VARIANTS.PRIMARY}
              className={
                isSelected
                  ? "api-dir-pkg_daab-plan-select-selected-plan-btn"
                  : "api-dir-pkg_daab-plan-card-btn"
              }
            />
          )}
        {isBundle && (
          <Button
            dataTestId="bundle-price-see-more"
            onClick={() => {
              openBundlePriceDialog(plan);
            }}
            text="See More"
            size={BUTTON_SIZES.STANDARD}
            variant={BUTTON_VARIANTS.TEXT}
            backgroundAppearance={
              isSelected
                ? BUTTON_BACKGROUND_APPEARANCES.PRIMARY
                : BUTTON_BACKGROUND_APPEARANCES.LIGHT
            }
            className="api-dir-pkg_daab-plan-card-btn"
          />
        )}
      </div>
    </div>
  );
};

const PlanDetails = ({ details, isBundle, planType }) => {
  const belongsTo = useDirectoryType();
  const {
    displayName,
    type,
    price,
    description,
    installFee,
    isDealerPlan
  } = details;

  const planTitle = getPlanName(type, belongsTo, displayName);

  const isBundleSubscription = planType === PLAN_TYPE.BUNDLE_SUBSCRIPTION;
  const isRegularSubscription = planType === PLAN_TYPE.SUBSCRIPTION;
  const isTransactional = planType === PLAN_TYPE.TRANSACTIONAL;

  // maintaining existing handling for api subscriptions in dealer directory
  const isLocationPlan =
    (isBundleSubscription && isDealerPlan) ||
    (isRegularSubscription &&
      belongsTo === API_DIRECTORY_THEME.DEALER_API_DIRECTORY);

  const priceRef = useRef(null);

  useEffect(() => {
    const el = priceRef.current;
    if (!el) return;

    const updateSize = () => {
      const text = el.textContent || "";
      const digits = (text.replace(/\D/g, "") || "").length;

      const baseFont = 28; 
      const extra = Math.max(0, digits - 10);
      const reduction = extra === 0 ? 0 : 2 * extra + (extra >= 1 ? 2 : 0);
      const fontSize = Math.max(12, baseFont - reduction);

      const lineHeight = fontSize === baseFont ? 40 : Math.max(14, Math.round(fontSize * 1.333));

      el.style.fontSize = `${fontSize}px`;
      el.style.lineHeight = `${lineHeight}px`;
    };

    updateSize();
    const observer = new MutationObserver(updateSize);
    observer.observe(el, { characterData: true, childList: true, subtree: true });
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [price]);

  const getUnitText = () => {
    if (isLocationPlan) return "/ Location";
    if (isTransactional) return "/ call";
    return "/ month";
  };

  return (
    <div className="api-dir-pkg_daab-plan-details">
      <div className="api-dir-pkg_daab-plan-name">{planTitle}</div>

      <div className="api-dir-pkg_daab-plan-amount">
        <div className="api-dir-pkg_per-dealer-location-wrapper">
          <span className="api-dir-pkg_per-dealer-location-currency">$</span>
          <span
            ref={priceRef}
            className="api-dir-pkg_per-dealer-location-price"
          >
            {price ? formatNumbers(price) : 0}
          </span>
          <span className="api-dir-pkg_per-dealer-location-unit">
            {getUnitText()}
          </span>
        </div>
        {isLocationPlan && (
          <div className="api-dir-pkg_per-dealer-location-info">
            <div className="api-dir-pkg_per-dealer-location-period">
              per month
            </div>
          </div>
        )}
      </div>
      {installFee != null && isBundle && (
        <div className="api-dir-pkg_daab-plan-install-fee">
          <span aria-hidden="true">+</span> {formatCurrency(installFee)} Install
          Fee
        </div>
      )}
      <div className="api-dir-pkg_daab-plan-description">{description}</div>
    </div>
  );
};

const EndpointContainers = ({
  plan,
  isAsync,
  allEndpoints,
  instanceIdVersionMap,
  expandContainerDetails,
  api,
  isBundlePricing,
  isBundle,
  fetchingAPIDetailsInBundle
}) => {
  const allowsExpansion =
    !isBundle || (isBundle && plan.planType !== PLAN_TYPE.ADVANCED);
  const [expandedContainer, setExpandedContainer] = useState(false);

  const onExpanded = index => {
    if (allowsExpansion) {
      if (isBundlePricing && isAsync && !allEndpoints?.length > 0) {
        expandContainerDetails({
          apiId: api?.id,
          apiType: api?.type
        });
      }
      setExpandedContainer(expandedContainer === index ? null : index);
    }
  };

  return (
    <div className="api-dir-pkg_daab-endpoint-containers">
      {plan.containers?.map((container, index) => (
        <Container
          pricingDetails={{
            title: container.name,
            quota: container.quota,
            overage: container.overage,
            endpoints: container.endPoints,
            channels: container.channels,
            isFree: container?.isFree
          }}
          index={index}
          key={index}
          allowsExpansion={allowsExpansion}
          isExpanded={index === expandedContainer}
          onExpanded={index => onExpanded(index)}
          allEndpoints={allEndpoints}
          isAsync={isAsync}
          instanceIdVersionMap={instanceIdVersionMap}
          fetchingAPIDetailsInBundle={fetchingAPIDetailsInBundle}
        />
      ))}
    </div>
  );
};

const Container = ({
  pricingDetails: { title, quota, overage, endpoints, channels, isFree },
  index,
  allowsExpansion,
  onExpanded,
  isExpanded,
  isAsync,
  allEndpoints = [],
  instanceIdVersionMap,
  fetchingAPIDetailsInBundle
}) => {
  const paths = isAsync ? channels : endpoints;
  const containerEndpoints =
    paths && paths.length
      ? constructEndpointTitle(paths, instanceIdVersionMap)
      : allEndpoints;
  const callsPerMonthText = `${
    isFree ? "Unlimited" : formatNumbers(quota)
  } free calls per month`;

  const overageText = `$${overage} overage fee`;

  return (
    <div className="api-dir-pkg_daab-endpoint-container">
      <div className="api-dir-pkg_daab-container-title">
        <IconCheckCircle className="api-dir-pkg_daab-check-icon" />
        <div className="api-dir-pkg_daab-container-title-text">{title}</div>
      </div>
      <div className="api-dir-pkg_daab-adv-pp-container-details">
        {allowsExpansion ? (
          isExpanded ? (
            <IconArrowDropDown
              onClick={() => onExpanded(index)}
              data-testid="api-dir-pkg_daab-collapse-icon"
              className="api-dir-pkg_daab-arrow-icon"
            />
          ) : (
            <IconArrowCascadeRight
              onClick={() => onExpanded(index)}
              data-testid="api-dir-pkg_daab-expand-icon"
              className="api-dir-pkg_daab-arrow-icon"
            />
          )
        ) : (
          <div className="api-dir-pkg_daab-container-filler" />
        )}
        <div className="api-dir-pkg_daab-adv-plan-container">
          <div>{callsPerMonthText}</div>
          <div>{overageText}</div>
        </div>
      </div>
      {isExpanded && (
        <div
          className="api-dir-pkg_daab-adv-plan-endpoint-list"
          data-testid="api-dir-pkg_daab-endpoint-list"
        >
          {fetchingAPIDetailsInBundle && (
            <div className="api-dir-pkg_daab-fetching-api-endpoints">
              {" "}
              <span data-testid="loader">Loading...</span>
            </div>
          )}
          {!fetchingAPIDetailsInBundle &&
            containerEndpoints.map((endpoint, index) => (
              <div key={index} className="api-dir-pkg_daab-endpoint-name">
                {endpoint}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const constructEndpointTitle = (endpoints, instanceIdVersionMap) => {
  return endpoints.map(ep => {
    let desc = ep.summary || ep.route?.replace("/", "");
    return `${ep?.verb?.padEnd(9, " ")} ${instanceIdVersionMap?.find(
      f => f.apiInstanceId === ep.apiInstanceId
    )?.version || ""}-${desc}`;
  });
};

const LegacyPlanContainers = ({ type, tiers }) => {
  const isBundleSubscription = type === PLAN_TYPE.BUNDLE_SUBSCRIPTION;
  const isRegularSubscription = type === PLAN_TYPE.SUBSCRIPTION;
  const isTransactional = type === PLAN_TYPE.TRANSACTIONAL;

  const callsPerMonthText = isBundleSubscription
    ? `Free calls per month per location`
    : isRegularSubscription
    ? `Free calls per month per location`
    : null;

  const overageText =
    (isBundleSubscription || isRegularSubscription) && "No overage fees";

  const renderSubscriptionFeatures = () => {
    if (isBundleSubscription) {
      return (
        <div className="api-dir-pkg_subscription-feature-row">
          <IconCheckCircle className="api-dir-pkg_subscription-feature-icon" />
          <div className="api-dir-pkg_subscription-feature-text">
            All Endpoints Included
          </div>
        </div>
      );
    }

    const features = [{ text: "All Endpoints Included" }];
    return features.map((feature, index) => (
      <div className="api-dir-pkg_subscription-feature-row" key={index}>
        <IconCheckCircle className="api-dir-pkg_subscription-feature-icon" />
        <div className="api-dir-pkg_subscription-feature-text">
          {feature.text}
        </div>
      </div>
    ));
  };

  return (
    <div className="api-dir-pkg_daab-endpoint-containers">
      <div className="api-dir-pkg_daab-endpoint-container">
        {(isBundleSubscription || isRegularSubscription) && (
          <>
            {renderSubscriptionFeatures()}
            <div className="api-dir-pkg_daab-subs-plan-container-quota-overage">
              <div>{callsPerMonthText}</div>
              <div>{overageText}</div>
            </div>
          </>
        )}

        {!isBundleSubscription && !isRegularSubscription && (
          <div className="api-dir-pkg_daab-container-title">
            <IconCheckCircle className="api-dir-pkg_daab-check-icon" />
            <div className="api-dir-pkg_daab-container-title-text">
              All Endpoints
            </div>
          </div>
        )}

        {isTransactional &&
          tiers?.map((tier, index) => {
            const rate = tier.pricePerRequest ?? tier.rate;
            const callsText = `Call ${formatNumbers(tier.startUnit)} - ${
              tier.endUnit ? formatNumbers(tier.endUnit) : "Unlimited"
            }`;
            const ratesText = `$${formatNumbers(rate)}/call`;

            return (
              <div className="api-dir-pkg_daab-plan-tiers" key={index}>
                <div className="api-dir-pkg_daab-plan-tiers-calls">
                  {callsText}
                </div>
                <div className="api-dir-pkg_daab-plan-tiers-rates">
                  {ratesText}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export { DAABPricingPlanTile };
